import React, { FC, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';

export type EmailOtpForUser = {
    email: string;
    code: string;
    expiresAt: string;
};

type EmailOtpDialogProps = {
    otp: EmailOtpForUser;
    onClose: () => void;
};

/**
 * Support tool: shows a freshly issued email OTP so a super admin can relay it
 * to a user whose code email never arrived.
 */
const EmailOtpDialog: FC<EmailOtpDialogProps> = ({ otp, onClose }: EmailOtpDialogProps) => {
    const { t: translate } = useTranslation(['users', 'common']);
    const [copied, setCopied] = useState(false);

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(otp.code);
            setCopied(true);
        } catch {
            setCopied(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth data-test="users.email-otp-dialog">
            <DialogTitle>{translate('users:EMAIL_OTP_DIALOG_TITLE')}</DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {translate('users:EMAIL_OTP_DIALOG_VERIFY_IDENTITY')}
                </Alert>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {otp.email}
                </Typography>
                <Box display="flex" alignItems="center" gap={2} my={2}>
                    <Typography
                        variant="h3"
                        component="div"
                        sx={{ letterSpacing: '0.4em', fontFamily: 'monospace' }}
                        data-test="users.email-otp-code"
                    >
                        {otp.code}
                    </Typography>
                    <Button size="small" startIcon={<ContentCopyIcon />} onClick={copyCode}>
                        {copied ? translate('common:COPIED') : translate('common:COPY')}
                    </Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    {translate('users:EMAIL_OTP_DIALOG_EXPIRES', {
                        time: new Date(otp.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    })}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{translate('common:CLOSE')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmailOtpDialog;
